<script lang="ts">
  import type { Snippet } from "svelte";

  /**
   * A plan, presented the way the marketing site presents one: name, price,
   * interval, what it includes, and one call to action.
   *
   * Presentation only — it owns no plan data, fetches nothing, and decides nothing
   * about eligibility. The caller supplies the strings and the action, which is why
   * the same primitive can back the upgrade banner, the Billing page's current-plan
   * summary, and an Overview tile without any of them agreeing on a data shape.
   *
   * It is a card in its own right (surface, hairline, --radius). Do not nest it in
   * a <Card>: two rounded panels inside one another is the one composition the brand
   * does not do.
   */
  interface Props {
    /** Plan name. Rendered as the card's heading and used to label the region. */
    name: string;
    /** Headline figure, e.g. "$30" or "Included". */
    price?: string;
    /** Qualifier under the price, e.g. "per user / month". */
    interval?: string;
    /** Bulleted inclusions. Each renders with a decorative check. */
    features?: string[];
    /**
     * Emphasised treatment: accent border, raised surface, and the site's featured
     * glow. Visual weight only — anything a screen reader must know belongs in
     * `flag` or the body.
     */
    featured?: boolean;
    /** Ribbon across the top edge, e.g. "Most popular". Real text, so AT reads it. */
    flag?: string;
    /**
     * Heading level for `name`. The default (2) suits a card sitting directly under
     * a page's h1; drop to 3 or 4 when it sits inside an already-headed section.
     */
    headingLevel?: 2 | 3 | 4;
    /** Free-form body between the features and the action. */
    children?: Snippet;
    /** The call to action. Left to the caller so this primitive stays presentational. */
    action?: Snippet;
  }
  const {
    name,
    price,
    interval,
    features,
    featured = false,
    flag,
    headingLevel = 2,
    children,
    action
  }: Props = $props();

  const uid = $props.id();
  const titleId = `${uid}-plan-name`;
</script>

<section class="plan-card" class:featured aria-labelledby={titleId}>
  {#if flag}<p class="plan-flag">{flag}</p>{/if}
  <svelte:element this={`h${headingLevel}`} id={titleId} class="plan-name">{name}</svelte:element>
  {#if price}<p class="plan-price tnum">{price}</p>{/if}
  {#if interval}<p class="plan-interval">{interval}</p>{/if}
  {#if features && features.length > 0}
    <ul class="plan-features">
      {#each features as feature (feature)}
        <li>
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
          <span>{feature}</span>
        </li>
      {/each}
    </ul>
  {/if}
  {#if children}<div class="plan-body">{@render children()}</div>{/if}
  {#if action}<div class="plan-action">{@render action()}</div>{/if}
</section>

<style>
  .plan-card {
    position: relative;
    display: flex;
    flex-direction: column;
    background: var(--surface);
    border: 1px solid var(--line-soft);
    border-radius: var(--radius);
    padding: 28px 24px;
    transition: border-color var(--dur-3) var(--ease);
  }
  .plan-card:hover {
    border-color: var(--line);
  }
  /* The site's featured plan: accent edge, the raised surface, and --glow-featured
     (a 1px accent ring plus a wide, low ambient lift). No second hue, no orb. */
  .plan-card.featured {
    border-color: var(--accent-border);
    background: var(--surface-2);
    box-shadow: var(--glow-featured);
    /* --muted (#7d8da0) clears AA on --bg (5.63:1) and on --surface (4.74:1) but not
       on the raised --surface-2 (4.29:1), which is the only place the brand pairs the
       two. Raising it to --text-soft (6.19:1) for this subtree fixes the card's own
       muted text AND anything a caller slots into it, which per-element overrides
       would keep missing. --text-soft is an existing promoted site literal, so the
       brand drift gate is untouched. */
    --muted: var(--text-soft);
  }
  .plan-flag {
    position: absolute;
    top: -11px;
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
    padding: 4px 12px;
    background: var(--accent);
    color: var(--accent-ink);
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0;
    white-space: nowrap;
  }
  .plan-name {
    margin: 0 0 12px;
    color: var(--accent);
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: uppercase;
  }
  .plan-price {
    margin: 0;
    color: var(--heading);
    font-size: clamp(1.625rem, 1.875rem, 2rem);
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1.1;
  }
  .plan-interval {
    margin: 6px 0 0;
    color: var(--muted);
    font-size: 13.5px;
  }
  .plan-features {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 11px;
    margin: 20px 0 0;
    padding: 0;
    flex: 1 1 auto;
  }
  .plan-features li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    color: var(--text);
    font-size: 14px;
  }
  .plan-features li svg {
    flex: none;
    width: 16px;
    height: 16px;
    margin-top: 3px;
    color: var(--accent);
  }
  .plan-body {
    margin-top: 16px;
    color: var(--muted);
    font-size: 13.5px;
  }
  /* Pinned to the bottom edge so a row of plans lines its actions up regardless of
     how many inclusions each one lists. */
  .plan-action {
    margin-top: auto;
    padding-top: 22px;
    display: flex;
    flex-direction: column;
  }
  /* The card's own CTA spans it, matching the site's full-width plan button. */
  .plan-action :global(.btn) {
    width: 100%;
    justify-content: center;
  }
</style>
